import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Form, Button, Alert, Card, Row, Col, Spinner, Badge } from 'react-bootstrap'
import {
  useAdminMunicipality,
  useCreateAdminMunicipality,
  useUpdateAdminMunicipality
} from 'api/hooks/use-admin-municipalities'
import { useAdminSections } from 'api/hooks/use-admin-sections'

const FISCAL_MONTHS = [
  { value: 1, label: '1 — January' },
  { value: 2, label: '2 — February' },
  { value: 3, label: '3 — March' },
  { value: 4, label: '4 — April' },
  { value: 5, label: '5 — May' },
  { value: 6, label: '6 — June' },
  { value: 7, label: '7 — July' },
  { value: 8, label: '8 — August' },
  { value: 9, label: '9 — September' },
  { value: 10, label: '10 — October' },
  { value: 11, label: '11 — November' },
  { value: 12, label: '12 — December' }
]

const REFRESH_SCHEDULES = [
  { value: 'none', label: 'None (manual only)' },
  { value: 'nightly', label: 'Nightly' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' }
]

const DAYS_OF_WEEK = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' }
]

const SQL_SERVER_VERSIONS = [
  { value: 2012, label: 'SQL Server 2012' },
  { value: 2014, label: 'SQL Server 2014' },
  { value: 2016, label: 'SQL Server 2016' },
  { value: 2017, label: 'SQL Server 2017' },
  { value: 2019, label: 'SQL Server 2019' },
  { value: 2022, label: 'SQL Server 2022' }
]

const buildInitialState = (municipality) => ({
  name: municipality?.name ?? '',
  abbreviation: municipality?.abbreviation ?? '',
  email_domain: municipality?.email_domain ?? '',
  it_contact_email: municipality?.it_contact_email ?? '',
  it_contact_first_name: municipality?.it_contact_first_name ?? '',
  it_contact_last_name: municipality?.it_contact_last_name ?? '',
  bucket_name: municipality?.bucket_name ?? '',
  output_bucket: municipality?.output_bucket ?? '',
  directory: municipality?.directory ?? 'data',
  powerbi_workspace_guid: municipality?.powerbi_workspace_guid ?? '',
  has_allocations: municipality?.has_allocations ?? false,
  is_custom_pcard: municipality?.is_custom_pcard ?? false,
  refresh_schedule: municipality?.refresh_schedule ?? 'none',
  refresh_day_of_week: municipality?.refresh_day_of_week ?? 1,
  refresh_day_of_month: municipality?.refresh_day_of_month ?? 1,
  sso_enabled: municipality?.sso_enabled ?? false,
  deployment_type: municipality?.deployment_type ?? '',
  sql_server_version: municipality?.sql_server_version ?? '',
  section_ids: municipality?.sections?.map((s) => s.id) ?? [],
  setting: {
    department_segment: municipality?.setting?.department_segment ?? '',
    fiscal_year_start_month: municipality?.setting?.fiscal_year_start_month ?? 1,
    fiscal_year_start_day: municipality?.setting?.fiscal_year_start_day ?? 1
  }
})

const MunicipalityForm = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const isEditing = !!id

  const { data: municipality, isLoading } = useAdminMunicipality({ id })
  const { data: sections } = useAdminSections()
  const createMutation = useCreateAdminMunicipality({
    options: {
      onSuccess: (data) => navigate(`/admin/municipalities/${data.id}`)
    }
  })
  const updateMutation = useUpdateAdminMunicipality({
    id,
    options: { onSuccess: () => navigate(`/admin/municipalities/${id}`) }
  })

  const [formData, setFormData] = useState(() => buildInitialState(null))

  useEffect(() => {
    if (municipality) {
      setFormData(buildInitialState(municipality))
    }
  }, [municipality])

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }))
  }

  const handleSettingChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      setting: { ...prev.setting, [name]: value }
    }))
  }

  const handleSectionToggle = (sectionId) => {
    setFormData((prev) => ({
      ...prev,
      section_ids: prev.section_ids.includes(sectionId)
        ? prev.section_ids.filter((sid) => sid !== sectionId)
        : [...prev.section_ids, sectionId]
    }))
  }

  const buildUpdatePayload = () => {
    const { section_ids: _omitted, ...rest } = formData
    return rest
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (isEditing) {
      updateMutation.mutate(buildUpdatePayload())
    } else {
      createMutation.mutate(formData)
    }
  }

  const sodSection = sections?.find((s) => s.abbreviation === 'SOD')
  const isSodSelected = sodSection && formData.section_ids.includes(sodSection.id)

  const error = createMutation.error || updateMutation.error
  const isSubmitting = createMutation.isLoading || updateMutation.isLoading

  if (isLoading && isEditing) {
    return (
      <div className="text-center p-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    )
  }

  return (
    <div>
      <h2>{isEditing ? 'Edit Municipality' : 'Create Municipality'}</h2>

      {error && (
        <Alert variant="danger" className="mt-3">
          {error.response?.data?.message || error.message || 'An error occurred'}
        </Alert>
      )}

      <Card className="mt-3">
        <Card.Header><h6 className="mb-0">Basic Information</h6></Card.Header>
        <Card.Body>
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Name *</Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Abbreviation</Form.Label>
                  <Form.Control
                    type="text"
                    name="abbreviation"
                    value={formData.abbreviation}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Email Domain</Form.Label>
                  <Form.Control
                    type="text"
                    name="email_domain"
                    value={formData.email_domain}
                    onChange={handleChange}
                    placeholder="e.g. wilmingtonde.gov"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>IT Contact First Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="it_contact_first_name"
                    value={formData.it_contact_first_name}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>IT Contact Last Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="it_contact_last_name"
                    value={formData.it_contact_last_name}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>IT Contact Email</Form.Label>
                  <Form.Control
                    type="email"
                    name="it_contact_email"
                    value={formData.it_contact_email}
                    onChange={handleChange}
                    placeholder="e.g. it-support@wilmingtonde.gov"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Directory</Form.Label>
                  <Form.Control
                    type="text"
                    name="directory"
                    value={formData.directory}
                    onChange={handleChange}
                    placeholder="data"
                  />
                </Form.Group>
              </Col>
              <Col md={8}>
                <Form.Group className="mb-3">
                  <Form.Label>PowerBI Workspace GUID</Form.Label>
                  <Form.Control
                    type="text"
                    name="powerbi_workspace_guid"
                    value={formData.powerbi_workspace_guid}
                    onChange={handleChange}
                    placeholder="optional — e.g. 11111111-2222-3333-4444-555555555555"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Card className="mb-3">
              <Card.Header><h6 className="mb-0">S3 Configuration</h6></Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Upload Bucket Name</Form.Label>
                      <Form.Control
                        type="text"
                        name="bucket_name"
                        value={formData.bucket_name}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Output Bucket Name</Form.Label>
                      <Form.Control
                        type="text"
                        name="output_bucket"
                        value={formData.output_bucket}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                {!isEditing && (
                  <Form.Text className="text-muted">
                    Bucket credentials are inherited automatically from an existing municipality.
                  </Form.Text>
                )}
              </Card.Body>
            </Card>

            <Card className="mb-3">
              <Card.Header><h6 className="mb-0">Initial Settings</h6></Card.Header>
              <Card.Body>
                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Department Segment</Form.Label>
                      <Form.Control
                        type="text"
                        name="department_segment"
                        value={formData.setting.department_segment}
                        onChange={handleSettingChange}
                        placeholder="e.g. seg_one"
                      />
                      <Form.Text className="text-muted">
                        Which segment of the chart-of-accounts represents the department.
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Fiscal Year Start Month</Form.Label>
                      <Form.Select
                        name="fiscal_year_start_month"
                        value={formData.setting.fiscal_year_start_month}
                        onChange={handleSettingChange}
                      >
                        {FISCAL_MONTHS.map((m) => (
                          <option key={m.value} value={m.value}>{m.label}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Fiscal Year Start Day</Form.Label>
                      <Form.Control
                        type="number"
                        min={1}
                        max={31}
                        name="fiscal_year_start_day"
                        value={formData.setting.fiscal_year_start_day}
                        onChange={handleSettingChange}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                {!isEditing && (
                  <Form.Text className="text-muted">
                    Additional settings (segment names, thresholds, branding) can be edited after creation
                    from the municipality detail page.
                  </Form.Text>
                )}
              </Card.Body>
            </Card>

            {!isEditing && (
              <Card className="mb-3">
                <Card.Header><h6 className="mb-0">Modules</h6></Card.Header>
                <Card.Body>
                  <Form.Text className="d-block mb-2 text-muted">
                    Select the modules being implemented for this municipality:
                  </Form.Text>
                  <div>
                    {sections?.map((section) => {
                      const isSelected = formData.section_ids.includes(section.id)
                      return (
                        <Badge
                          key={section.id}
                          bg={isSelected ? 'primary' : 'light'}
                          text={isSelected ? 'white' : 'primary'}
                          className="me-2 mb-2"
                          style={{ cursor: 'pointer', border: '1px solid #dee2e6', fontSize: '0.85rem' }}
                          role="button"
                          tabIndex={0}
                          aria-pressed={isSelected}
                          onClick={() => handleSectionToggle(section.id)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault()
                              handleSectionToggle(section.id)
                            }
                          }}
                        >
                          {section.name} ({section.abbreviation})
                        </Badge>
                      )
                    })}
                  </div>
                  {isSodSelected && (
                    <Form.Text className="d-block mt-2 text-muted">
                      SOD core analytics will be enabled automatically on create.
                    </Form.Text>
                  )}
                </Card.Body>
              </Card>
            )}

            <Card className="mb-3">
              <Card.Header><h6 className="mb-0">Options</h6></Card.Header>
              <Card.Body>
                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Deployment Type</Form.Label>
                      <Form.Select
                        name="deployment_type"
                        value={formData.deployment_type}
                        onChange={handleChange}
                      >
                        <option value="">Unknown / Not set</option>
                        <option value="cloud">Cloud</option>
                        <option value="on_prem">On-Premises</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>SQL Server Version</Form.Label>
                      <Form.Select
                        name="sql_server_version"
                        value={formData.sql_server_version}
                        onChange={handleChange}
                      >
                        <option value="">Unknown / Not set</option>
                        {SQL_SERVER_VERSIONS.map((v) => (
                          <option key={v.value} value={v.value}>{v.label}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Data Refresh Schedule</Form.Label>
                      <Form.Select
                        name="refresh_schedule"
                        value={formData.refresh_schedule}
                        onChange={handleChange}
                      >
                        {REFRESH_SCHEDULES.map((s) => (
                          <option key={s.value} value={s.value}>{s.label}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  {formData.refresh_schedule === 'weekly' && (
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Day of Week</Form.Label>
                        <Form.Select
                          name="refresh_day_of_week"
                          value={formData.refresh_day_of_week}
                          onChange={handleChange}
                        >
                          {DAYS_OF_WEEK.map((d) => (
                            <option key={d.value} value={d.value}>{d.label}</option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  )}
                  {formData.refresh_schedule === 'monthly' && (
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Day of Month</Form.Label>
                        <Form.Control
                          type="number"
                          min={1}
                          max={31}
                          name="refresh_day_of_month"
                          value={formData.refresh_day_of_month}
                          onChange={handleChange}
                          required
                        />
                        <Form.Text className="text-muted">
                          Days past the end of a short month run on its last day.
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  )}
                </Row>
                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Check
                        type="checkbox"
                        name="has_allocations"
                        label="Has Allocations"
                        checked={formData.has_allocations}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Check
                        type="checkbox"
                        name="is_custom_pcard"
                        label="Custom PCard"
                        checked={formData.is_custom_pcard}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Check
                        type="checkbox"
                        name="sso_enabled"
                        label="SSO Enabled"
                        checked={formData.sso_enabled}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            <div className="d-flex justify-content-between mt-4">
              <div>
                <Button type="submit" variant="primary" disabled={isSubmitting} className="me-2">
                  {isSubmitting ? 'Saving...' : (isEditing ? 'Update' : 'Create')}
                </Button>
                <Button
                  type="button"
                  variant="light"
                  className="text-primary"
                  onClick={() => navigate(isEditing ? `/admin/municipalities/${id}` : '/admin/municipalities')}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </div>
  )
}

export default MunicipalityForm
